import { type QueryKey, useMutation } from "@tanstack/react-query";
import { defineMessages, useIntl } from "react-intl";

import { QueryKeys, queryClient } from "@/api/core";
import { GroupMemberQueryKeys } from "@/api/group-members/query-keys";
import { useApi } from "@/hooks";
import toast from "@/toast";
import {
  removePageItem,
  removeAccountStatusesFromTimeline,
} from "@/utils/queries";

import type { Account, Group, GroupMember } from "@/schemas";

const messages = defineMessages({
  blocked: {
    id: "group.group_mod_block.success",
    defaultMessage: "@{name} is banned",
  },
});

function useBlockGroupMember(
  group: Group,
  account: Account,
  groupMember?: GroupMember,
) {
  const api = useApi();
  const intl = useIntl();

  return useMutation({
    mutationFn: () =>
      api.post(`/api/v1/groups/${group.id}/blocks`, {
        account_ids: [account.id],
      }),
    onMutate() {
      if (groupMember) {
        const queryData = queryClient.getQueriesData({
          queryKey: GroupMemberQueryKeys.groupMemberWithoutSearch(
            group.id,
            groupMember.role,
          ),
          type: "active",
        });
        const [prevCacheKey, prevGroupMembers] = queryData.flat() as [
          QueryKey,
          unknown,
        ];

        removePageItem(
          prevCacheKey,
          groupMember.id,
          (o: any, n: any) => o.id === n,
        );

        return { prevCacheKey, prevGroupMembers };
      }
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.accounts.groupBlocks(group.id),
      });

      // Remove statuses belonging to the now banned user
      removeAccountStatusesFromTimeline(
        QueryKeys.statuses.groupTimeline(group.id, true),
        account.id,
      );
      removeAccountStatusesFromTimeline(
        QueryKeys.statuses.groupTimeline(group.id, false),
        account.id,
      );

      toast.success(
        intl.formatMessage(messages.blocked, { name: account.username }),
      );
    },
    onError(_error, _variables, context) {
      if (context && context.prevCacheKey && context.prevGroupMembers) {
        queryClient.setQueryData(
          context.prevCacheKey,
          context.prevGroupMembers,
        );
      }
    },
  });
}

export { useBlockGroupMember };
